import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import {
  Building2,
  MoreVertical,
  Play,
  Pause,
  Trash2,
  UserCog,
  CheckCircle,
  XCircle,
  AlertCircle,
  CreditCard,
  Banknote,
  KeyRound,
  Loader2,
} from "lucide-react";
import { updateOpacIntegrationsConfigSuperAdmin } from "@/lib/api/opac-applications";
import { OpacPublicKeysSection } from "@/components/integrations/OpacPublicKeysSection";
import { cn } from "@/lib/utils";

interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: string;
  createdAt: string;
  trialStartDate?: string | null;
  trialEndDate?: string | null;
  customDomain?: string | null;
  billingPlanId?: string | null;
}

interface BillingPlan {
  id: string;
  name: string;
  price: number | string;
  currency: string;
  billingCycle: string;
  features?: any;
}

export function TenantManagementPage() {
  const { user } = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [billingPlans, setBillingPlans] = useState<BillingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showAssignPlanModal, setShowAssignPlanModal] = useState<string | null>(null);
  const [viewingTenant, setViewingTenant] = useState<any | null>(null);
  const [showChangePlanModal, setShowChangePlanModal] = useState<string | null>(null);
  const [showSubaccountModal, setShowSubaccountModal] = useState<string | null>(null);
  const [subaccountForm, setSubaccountForm] = useState({
    businessName: "",
    bankCode: "",
    accountNumber: "",
    bankName: "",
  });
  const [platformFeePercent, setPlatformFeePercent] = useState<number>(0);
  const [paymentConfigSaving, setPaymentConfigSaving] = useState(false);
  const [opacKeySelfService, setOpacKeySelfService] = useState(true);
  const [opacConfigSaving, setOpacConfigSaving] = useState(false);

  useEffect(() => {
    loadTenants();
    loadBillingPlans();
  }, []);

  const loadTenants = async () => {
    try {
      const response = await api.get("/super-admin/tenants");
      setTenants(response.data);
    } catch (error) {
      console.error("Failed to load tenants:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadBillingPlans = async () => {
    try {
      const response = await api.get("/billing/plans/templates");
      setBillingPlans(response.data);
    } catch (error) {
      console.error("Failed to load billing plans:", error);
    }
  };

  const handleAssignPlan = async (tenantId: string, planId: string) => {
    try {
      await api.post(`/billing/plans/${planId}/assign`, { tenantId });
      await loadTenants();
      setShowAssignPlanModal(null);
      alert("Billing plan assigned successfully. The plan starts today (payment date).");
    } catch (error: any) {
      alert(error.response?.data?.message || "Failed to assign billing plan");
    }
  };

  const handleViewTenant = async (tenantId: string) => {
    try {
      const response = await api.get(`/super-admin/tenants/${tenantId}`);
      const tenant = response.data;
      setViewingTenant(tenant);
      const pc = (tenant.settings as any)?.paymentConfig || {};
      setPlatformFeePercent(pc.platformFeePercent ?? 0);
      const integrations = (tenant.settings as any)?.integrations || {};
      setOpacKeySelfService(integrations.opacKeySelfService !== false);
    } catch (error: any) {
      alert(error.response?.data?.message || "Failed to load tenant details");
    }
  };

  const handleCreateSubaccount = async (tenantId: string) => {
    try {
      await api.post(`/super-admin/tenants/${tenantId}/subaccount`, subaccountForm);
      await handleViewTenant(tenantId);
      setShowSubaccountModal(null);
      setSubaccountForm({ businessName: "", bankCode: "", accountNumber: "", bankName: "" });
      alert("Paystack subaccount created successfully");
    } catch (error: any) {
      alert(error.response?.data?.message || "Failed to create subaccount");
    }
  };

  const handleUpdatePaymentConfig = async (tenantId: string) => {
    setPaymentConfigSaving(true);
    try {
      await api.patch(`/super-admin/tenants/${tenantId}/payment-config`, {
        platformFeePercent,
      });
      await handleViewTenant(tenantId);
      alert("Payment config updated");
    } catch (error: any) {
      alert(error.response?.data?.message || "Failed to update payment config");
    } finally {
      setPaymentConfigSaving(false);
    }
  };

  const handleUpdateOpacConfig = async (tenantId: string) => {
    setOpacConfigSaving(true);
    try {
      await updateOpacIntegrationsConfigSuperAdmin(tenantId, opacKeySelfService);
      await handleViewTenant(tenantId);
      alert("OPAC integrations config updated");
    } catch (error: any) {
      alert(error.response?.data?.message || "Failed to update OPAC config");
    } finally {
      setOpacConfigSaving(false);
    }
  };

  const handleChangePlan = async (tenantId: string, planId: string) => {
    try {
      await api.patch(`/super-admin/tenants/${tenantId}/billing-plan`, { planId });
      await loadTenants();
      if (viewingTenant?.id === tenantId) {
        await handleViewTenant(tenantId);
      }
      setShowChangePlanModal(null);
      alert("Tenant billing plan changed successfully. The plan starts today (payment date).");
    } catch (error: any) {
      alert(error.response?.data?.message || "Failed to change tenant plan");
    }
  };

  const handleStatusChange = async (
    tenantId: string,
    action: "activate" | "suspend" | "disable" | "delete"
  ) => {
    setActionLoading(tenantId);
    try {
      if (action === "activate") {
        await api.patch(`/super-admin/tenants/${tenantId}/activate`);
      } else if (action === "suspend") {
        await api.patch(`/super-admin/tenants/${tenantId}/suspend`, {});
      } else if (action === "disable") {
        await api.patch(`/super-admin/tenants/${tenantId}/disable`, {});
      } else if (action === "delete") {
        if (
          !confirm("Are you sure you want to delete this tenant? This action cannot be undone.")
        ) {
          return;
        }
        await api.delete(`/super-admin/tenants/${tenantId}`, {});
      }
      await loadTenants();
      setSelectedTenant(null);
    } catch (error: any) {
      alert(error.response?.data?.message || "Failed to update tenant");
    } finally {
      setActionLoading(null);
    }
  };

  const handleImpersonate = async (tenantId: string) => {
    try {
      // Find a tenant admin user for this tenant
      const tenant = tenants.find((t) => t.id === tenantId);
      if (!tenant) return;

      // Get users for this tenant
      const usersResponse = await api.get("/super-admin/users");
      const tenantAdmin = usersResponse.data.find((u: any) =>
        u.Memberships?.some(
          (m: any) =>
            m.tenantId === tenantId && (m.role === "TENANT_ADMIN" || m.roleName === "Tenant Admin")
        )
      );

      if (!tenantAdmin) {
        alert("No tenant admin found for this tenant");
        return;
      }

      const impersonateResponse = await api.post(
        `/super-admin/users/${tenantAdmin.id}/impersonate`,
        {
          tenantId,
        }
      );

      // Cookie set by server — redirect to dashboard (full reload to pick up new session)
      sessionStorage.setItem("impersonated_by", user?.id || "");
      window.location.href = "/dashboard";
    } catch (error: any) {
      alert(error.response?.data?.message || "Failed to impersonate tenant");
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { label: "Active", className: "bg-green-100 text-green-800", icon: CheckCircle },
      suspended: {
        label: "Suspended",
        className: "bg-yellow-100 text-yellow-800",
        icon: AlertCircle,
      },
      disabled: { label: "Disabled", className: "bg-red-100 text-red-800", icon: XCircle },
      trial: { label: "Trial", className: "bg-primary/10 text-primary", icon: AlertCircle },
      expired: { label: "Expired", className: "bg-gray-100 text-gray-800", icon: XCircle },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.active;
    const Icon = config.icon;

    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
          config.className
        )}
      >
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Manage Tenants"
        description="View and manage all tenant accounts"
        breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "Manage Tenants" }]}
        showSearch={false}
        titleSize="md"
      />

      <Card className="p-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Tenant</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Status</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Created</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => (
                <tr key={tenant.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Building2 className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{tenant.name}</p>
                        <p className="text-sm text-gray-600">{tenant.slug}</p>
                        {tenant.customDomain && (
                          <p className="text-xs text-gray-500">{tenant.customDomain}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">{getStatusBadge(tenant.status)}</td>
                  <td className="py-4 px-4 text-sm text-gray-600">
                    {new Date(tenant.createdAt).toLocaleDateString()}
                  </td>
                  <td className="py-4 px-4">
                    <div className="relative">
                      <button
                        onClick={() =>
                          setSelectedTenant(selectedTenant === tenant.id ? null : tenant.id)
                        }
                        className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                      >
                        <MoreVertical className="w-4 h-4 text-gray-600" />
                      </button>
                      {selectedTenant === tenant.id && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                          <div className="py-1">
                            {tenant.status !== "active" && (
                              <button
                                onClick={() => handleStatusChange(tenant.id, "activate")}
                                disabled={actionLoading === tenant.id}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <Play className="w-4 h-4" />
                                Activate
                              </button>
                            )}
                            {tenant.status === "active" && (
                              <>
                                <button
                                  onClick={() => handleStatusChange(tenant.id, "suspend")}
                                  disabled={actionLoading === tenant.id}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                >
                                  <Pause className="w-4 h-4" />
                                  Suspend
                                </button>
                                <button
                                  onClick={() => handleStatusChange(tenant.id, "disable")}
                                  disabled={actionLoading === tenant.id}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                >
                                  <XCircle className="w-4 h-4" />
                                  Disable
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleViewTenant(tenant.id)}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                            >
                              <Building2 className="w-4 h-4" />
                              View Details
                            </button>
                            <button
                              onClick={() => handleImpersonate(tenant.id)}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                            >
                              <UserCog className="w-4 h-4" />
                              Impersonate
                            </button>
                            <button
                              onClick={() => setShowAssignPlanModal(tenant.id)}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                            >
                              <CreditCard className="w-4 h-4" />
                              Assign Billing Plan
                            </button>
                            <hr className="my-1 border-gray-200" />
                            <button
                              onClick={() => handleStatusChange(tenant.id, "delete")}
                              disabled={actionLoading === tenant.id}
                              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Assign Billing Plan Modal */}
      {showAssignPlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6 m-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Assign Billing Plan</h2>
            <p className="text-sm text-gray-600 mb-4">
              Select a billing plan to assign to this tenant. The start date will be set to today
              (payment date).
            </p>
            {billingPlans.length === 0 ? (
              <div className="text-center py-8">
                <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No billing plans available</p>
                <p className="text-sm text-gray-500 mt-2">
                  Create billing plans first from the Billing Plans page
                </p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {billingPlans.map((plan) => (
                  <div
                    key={plan.id}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleAssignPlan(showAssignPlanModal, plan.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{plan.name}</p>
                        <p className="text-sm text-gray-600">
                          {plan.currency}{" "}
                          {typeof plan.price === "number"
                            ? plan.price.toLocaleString()
                            : plan.price}{" "}
                          / {plan.billingCycle}
                        </p>
                      </div>
                      <Button size="sm">Assign</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-6">
              <Button
                variant="outline"
                onClick={() => setShowAssignPlanModal(null)}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* View Tenant Details Modal */}
      {viewingTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl p-6 m-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Tenant Details</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <p className="text-gray-900">{viewingTenant.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Slug</label>
                  <p className="text-gray-900">{viewingTenant.slug}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <p className="text-gray-900 capitalize">{viewingTenant.status}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Created</label>
                  <p className="text-gray-900">
                    {new Date(viewingTenant.createdAt).toLocaleDateString()}
                  </p>
                </div>
                {viewingTenant.customDomain && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Custom Domain</label>
                    <p className="text-gray-900">{viewingTenant.customDomain}</p>
                  </div>
                )}
                {viewingTenant.BillingPlans && viewingTenant.BillingPlans.length > 0 && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Billing Plan
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-medium text-gray-900">
                        {viewingTenant.BillingPlans[0].name} -{" "}
                        {viewingTenant.BillingPlans[0].currency}{" "}
                        {typeof viewingTenant.BillingPlans[0].price === "number"
                          ? viewingTenant.BillingPlans[0].price.toLocaleString()
                          : viewingTenant.BillingPlans[0].price}{" "}
                        / {viewingTenant.BillingPlans[0].billingCycle}
                      </p>
                      {viewingTenant.BillingPlans[0].endDate && (
                        <p className="text-sm text-gray-600 mt-1">
                          Expires:{" "}
                          {new Date(viewingTenant.BillingPlans[0].endDate).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* Payment / Settlement Config */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Banknote className="w-4 h-4" />
                  Payment & Settlement
                </label>
                <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                  {((viewingTenant.settings as any)?.paymentConfig as any)?.subaccountCode ? (
                    <div>
                      <p className="text-sm text-green-700 font-medium">
                        Paystack subaccount configured
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Subaccount: {(viewingTenant.settings as any).paymentConfig.subaccountCode}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-600">No Paystack subaccount</p>
                      <Button
                        size="sm"
                        className="mt-2"
                        onClick={() => setShowSubaccountModal(viewingTenant.id)}
                      >
                        Create Subaccount
                      </Button>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Platform Fee (%)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        value={platformFeePercent}
                        onChange={(e) => setPlatformFeePercent(Number(e.target.value))}
                        className="w-24 px-2 py-1 border rounded text-sm"
                      />
                      <Button
                        size="sm"
                        onClick={() => handleUpdatePaymentConfig(viewingTenant.id)}
                        disabled={paymentConfigSaving}
                        className="gap-2"
                      >
                        {paymentConfigSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        Save
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Percentage of each fee that goes to the platform
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <KeyRound className="w-4 h-4" />
                  Public OPAC API
                </label>
                <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={opacKeySelfService}
                      onChange={(e) => setOpacKeySelfService(e.target.checked)}
                    />
                    Allow tenant admin to create and rotate OPAC keys in Setup → Integrations
                  </label>
                  <Button
                    size="sm"
                    onClick={() => handleUpdateOpacConfig(viewingTenant.id)}
                    disabled={opacConfigSaving}
                    className="gap-2"
                  >
                    {opacConfigSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Save OPAC access setting
                  </Button>
                  <OpacPublicKeysSection
                    tenantId={viewingTenant.id}
                    superAdminMode
                    selfServiceEnabled={true}
                    publicOpacPath="/api/v1/public/opac"
                  />
                </div>
              </div>
              {viewingTenant._count && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Statistics</label>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">Books</p>
                      <p className="text-xl font-bold text-gray-900">
                        {viewingTenant._count.Books || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">Members</p>
                      <p className="text-xl font-bold text-gray-900">
                        {viewingTenant._count.Memberships || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">Checkouts</p>
                      <p className="text-xl font-bold text-gray-900">
                        {viewingTenant._count.Checkouts || 0}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex gap-2 pt-4">
                <Button
                  onClick={() => {
                    setShowChangePlanModal(viewingTenant.id);
                  }}
                  className="flex-1"
                >
                  Change Billing Plan
                </Button>
                <Button variant="outline" onClick={() => setViewingTenant(null)} className="flex-1">
                  Close
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Create Subaccount Modal */}
      {showSubaccountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6 m-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Create Paystack Subaccount</h2>
            <p className="text-sm text-gray-600 mb-4">
              Add this library's bank account as a Paystack subaccount for registration fee
              settlements.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Name
                </label>
                <input
                  type="text"
                  value={subaccountForm.businessName}
                  onChange={(e) =>
                    setSubaccountForm({ ...subaccountForm, businessName: e.target.value })
                  }
                  placeholder="Library/University name"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bank Code</label>
                <input
                  type="text"
                  value={subaccountForm.bankCode}
                  onChange={(e) =>
                    setSubaccountForm({ ...subaccountForm, bankCode: e.target.value })
                  }
                  placeholder="e.g. 058 for GTBank"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Number
                </label>
                <input
                  type="text"
                  value={subaccountForm.accountNumber}
                  onChange={(e) =>
                    setSubaccountForm({ ...subaccountForm, accountNumber: e.target.value })
                  }
                  placeholder="10-digit NUBAN"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button
                onClick={() => handleCreateSubaccount(showSubaccountModal)}
                disabled={
                  !subaccountForm.businessName ||
                  !subaccountForm.bankCode ||
                  !subaccountForm.accountNumber
                }
              >
                Create
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowSubaccountModal(null);
                  setSubaccountForm({
                    businessName: "",
                    bankCode: "",
                    accountNumber: "",
                    bankName: "",
                  });
                }}
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Change Plan Modal */}
      {showChangePlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6 m-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Change Tenant Billing Plan</h2>
            <p className="text-sm text-gray-600 mb-4">
              Select a billing plan to change the tenant's subscription. The plan will start today
              (payment date).
            </p>
            {billingPlans.length === 0 ? (
              <div className="text-center py-8">
                <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No billing plans available</p>
                <p className="text-sm text-gray-500 mt-2">
                  Create billing plans first from the Billing Plans page
                </p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {billingPlans.map((plan) => (
                  <div
                    key={plan.id}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleChangePlan(showChangePlanModal, plan.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{plan.name}</p>
                        <p className="text-sm text-gray-600">
                          {plan.currency}{" "}
                          {typeof plan.price === "number"
                            ? plan.price.toLocaleString()
                            : plan.price}{" "}
                          / {plan.billingCycle}
                        </p>
                      </div>
                      <Button size="sm">Change</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-6">
              <Button
                variant="outline"
                onClick={() => setShowChangePlanModal(null)}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}
    </PageContainer>
  );
}
